import React, { useCallback, useMemo, useRef, useState } from 'react';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { Button, ButtonProps, ButtonWithGAClickButton } from '../button';
import { GalleryModal } from '../gallery-modal';
import { DEFAULT_IMAGE_TRANSFORMATIONS, Picture } from '../picture';
import {
  GalleryGrid,
  galleryImage,
  galleryImageDefaultWrapper,
  GalleryImageWrapper,
  showMoreBtnWrapper,
} from './gallery-styles';
import { GalleryImage } from './gallery-types';
import { ImageName } from '../image-name';

type GalleryProps = {
  galleryGridClassName?: string;
  galleryImageClassName?: string;
  elementBlock: string;
  elementText: string;
  imageList: GalleryImage[];
  imagePerPage: number;
  initialImagePerPage?: number;
  isShowImageName?: boolean;
  isShowImageAddText?: boolean;
  promoElementBlock?: string;
  renderGalleryBlock?: (index: number) => React.ReactNode;
  showMoreBtnLabel?: string;
  modalBtnLabel?: string;
  withShowMoreBtn?: boolean;
  modalBtnProps?: Omit<ButtonProps, 'onClick' | 'iconOnly'> & { onClick: () => void };
};

export const Gallery: React.FC<GalleryProps> = ({
  elementBlock,
  elementText,
  galleryGridClassName,
  galleryImageClassName,
  imageList,
  imagePerPage,
  initialImagePerPage,
  isShowImageName,
  isShowImageAddText,
  modalBtnProps,
  promoElementBlock,
  renderGalleryBlock,
  showMoreBtnLabel,
  modalBtnLabel,
  withShowMoreBtn = true,
}) => {
  const bannerRefs = useRef<HTMLDivElement[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [lastRefIndex, setLastRefIndex] = useState<number>(0);
  const [visibleImage, setVisibleImage] = useState(initialImagePerPage || imagePerPage);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const galleryImageList = useMemo(() => imageList.slice(0, visibleImage), [imageList, visibleImage]);

  const isShowMoreBtnVisible = useMemo(() => withShowMoreBtn && visibleImage < imageList.length, [
    imageList,
    visibleImage,
    withShowMoreBtn,
  ]);

  const promotions = useMemo(
    () =>
      imageList.map((image, index: number) => ({
        name: `${elementBlock}_bottom`,
        creative: image.name,
        position: (index + 1).toString(),
      })),
    [elementBlock, imageList]
  );

  usePromoViewEvent(bannerRefs, promoElementBlock || `${elementBlock} / gallery`, promotions, lastRefIndex);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setActiveImageIndex(0);
  }, [setIsModalOpen, setActiveImageIndex]);

  const handleGalleryModalBtnClick = useCallback(() => {
    if (modalBtnProps && modalBtnProps.onClick) {
      modalBtnProps.onClick();
    }
    handleCloseModal();
  }, [modalBtnProps, handleCloseModal]);

  const handleShowMoreBtnClick = useCallback(() => {
    setLastRefIndex(visibleImage);
    setVisibleImage(visibleImage + imagePerPage);
    analyticsUtils.clickButtonEvent(elementBlock, 'Показать ещё', elementText);
  }, [elementBlock, elementText, imagePerPage, visibleImage]);

  const handleImageClick = useCallback(
    (index: number) => () => {
      setIsModalOpen(true);
      setActiveImageIndex(index);
      analyticsUtils.promoClickEvent(promoElementBlock || `${elementBlock} / gallery`, promotions[index]);
    },
    [elementBlock, promoElementBlock, promotions]
  );

  return (
    <>
      <GalleryGrid className={galleryGridClassName}>
        {galleryImageList.map(({ srcSmall, alt, transformations, addText }: GalleryImage, index: number) => (
          <>
            <GalleryImageWrapper
              className={galleryImageClassName || galleryImageDefaultWrapper}
              data-testid="gallery-image"
              data-position={index}
              key={createElementKey('ceilings-gallery', index)}
              onClick={handleImageClick(index)}
              ref={(el: HTMLDivElement | null) => {
                if (el && bannerRefs) bannerRefs.current[index] = el;
              }}>
              <Picture
                alt={alt}
                className={galleryImage}
                loading="lazy"
                src={srcSmall}
                title={alt}
                transformations={transformations || DEFAULT_IMAGE_TRANSFORMATIONS}
              />
              {isShowImageName && (
                <ImageName addText={addText} isShowImageAddText={isShowImageAddText} name={alt} size="m" />
              )}
            </GalleryImageWrapper>
            {renderGalleryBlock && renderGalleryBlock(index)}
          </>
        ))}
      </GalleryGrid>

      {isShowMoreBtnVisible && (
        <div className={showMoreBtnWrapper}>
          <Button onClick={handleShowMoreBtnClick} rounded>
            {showMoreBtnLabel || 'Показать ещё'}
          </Button>
        </div>
      )}

      <GalleryModal
        clickedImageIndex={activeImageIndex}
        elementBlock={elementBlock}
        elementText={`${elementText} / modal`}
        galleryList={imageList}
        onClose={handleCloseModal}
        renderFooterButton={
          modalBtnProps
            ? () => (
              <ButtonWithGAClickButton
                {...modalBtnProps}
                elementBlock={elementBlock}
                elementName="Записаться на консультацию"
                elementText={`${elementText} / modal`}
                invert
                variant="accent"
                onClick={handleGalleryModalBtnClick}>
                {modalBtnLabel || 'Записаться на консультацию'}
              </ButtonWithGAClickButton>
            )
            : undefined
        }
        isShowImageName={isShowImageName}
        isShowImageAddText={isShowImageAddText}
        show={isModalOpen}
      />
    </>
  );
};
